import React from 'react';
import { Typography, Spin, Tag, Tooltip, Button, Skeleton, Popconfirm, message } from 'antd';
import {
  FileText,
  ExternalLink,
  Trash2,
  BarChart2,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  Sparkles,
  Link as LinkIcon
} from 'lucide-react';
import { tokens } from '../theme/tokens';
import { useSocialContext } from '../context/social';
import { isPublishable } from '../types/social';
import { humanizePublishError } from '../utils/humanize-publish-error';
import { SumarPostsModal } from '../components/sumar-posts-modal';
import { SPONSOR_NAME } from '../config/brand';

const { Title, Text } = Typography;
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

// El backend gatea el DELETE de una publicación con require_open_id + ownership
// check (la task es tuya sólo si su social_id == tu open_id). Sin este header el
// endpoint responde 401/403 y el borrado no se aplica. Mismo JWT que el resto de
// la app (context/social.tsx, admin-page.tsx).
const JWT_KEY = 'drupi_social_jwt';
const authHeaders = (): Record<string, string> => {
  const jwt = localStorage.getItem(JWT_KEY);
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
};

interface PublishTask {
  _id: string;
  platform: 'instagram' | 'tiktok' | 'fake';
  social_id: string;
  status: 'pending' | 'publishing' | 'done' | 'failed';
  result_url: string | null;
  platform_post_id: string | null;
  caption: string;
  error: string | null;
  created_at: string;
  catalog_video_id?: string;
  catalog_video_url?: string;
  thumbnail_url?: string;
  // Invalidada por la marca desde brands (brand-admin) → se muestra invalidada acá también.
  moderated?: boolean;
  // Motivo de la invalidación que eligió la marca (se muestra en el thumb invalidado).
  moderated_reason?: string;
}

interface Metrics {
  fetched_at: string;
  ig_plays?: number;
  ig_reach?: number;
  ig_likes?: number;
  ig_comments?: number;
  ig_shares?: number;
  ig_saved?: number;
  ig_total_interactions?: number;
  tt_view_count?: number;
  tt_like_count?: number;
  tt_comment_count?: number;
  tt_share_count?: number;
  // Puntos que esta publicación aporta este mes (lo calcula el backend con la misma
  // fórmula que el ranking: vistas ganadas desde max(registro, inicio_de_mes)).
  points?: number;
}

const STATUS_COLOR: Record<string, string> = {
  pending: '#888',
  publishing: '#faad14',
  done: '#52c41a',
  failed: '#ff4d4f'
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendiente',
  publishing: 'Publicando...',
  done: 'Publicado',
  failed: 'Error'
};

const PLATFORM_STYLE: Record<string, { bg: string; border: string; text: string }> = {
  instagram: { bg: '#E1306C22', border: '#E1306C55', text: '#E1306C' },
  tiktok: { bg: '#69C9D022', border: '#69C9D055', text: '#010101' },
  fake: { bg: '#6366f122', border: '#6366f155', text: '#4f46e5' }
};

const Stat: React.FC<{ icon: React.ReactNode; label: string; value: number | undefined }> = ({
  icon,
  label,
  value
}) => {
  const displayValue = value ?? 0;
  return (
    <Tooltip title={label}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ color: tokens.colors.primary.neutral[400] }}>{icon}</span>
        <Text style={{ color: tokens.colors.primary.neutral[800], fontWeight: 600, fontSize: 14 }}>
          {displayValue.toLocaleString()}
        </Text>
      </div>
    </Tooltip>
  );
};

/**
 * Tres modos:
 *   1. `loading` → la metadata todavía está siendo traída por la Lambda
 *      (tarea externa recién creada, thumbnail_url aún vacío).
 *   2. Imagen (`thumbnail_url` viene de TikTok CDN / Apify) → <img>.
 *      ANTES tirábamos <video src=...> con URL de imagen, lo que dejaba el
 *      cuadro en negro porque ningún codec parseaba un .webp.
 *   3. Video (`catalog_video_url` apunta a B2) → <video>.
 *   4. Nada → placeholder gris con triángulo play.
 */
const TaskThumbnail: React.FC<{ task: PublishTask; loading?: boolean }> = ({ task, loading }) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const imageUrl = task.thumbnail_url;
  const videoUrl = !imageUrl ? task.catalog_video_url : undefined;

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoUrl) return;
    v.currentTime = 0.5;
  }, [videoUrl]);

  if (loading) {
    return <Skeleton.Image active style={{ width: 52, height: 92, borderRadius: 8, flexShrink: 0 }} />;
  }

  // Publicación invalidada por la marca: ocultamos el thumb real.
  if (task.moderated) {
    return (
      <div
        title={task.moderated_reason ? `Motivo: ${task.moderated_reason}` : undefined}
        style={{
          width: 52,
          height: 92,
          borderRadius: 8,
          flexShrink: 0,
          background: '#fff1f0',
          border: '1px solid #ffccc7',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          fontSize: 8,
          color: '#cf1322',
          padding: 4,
          lineHeight: 1.2,
          overflow: 'hidden'
        }}
      >
        <span>Esta publicación ha sido invalidada</span>
        {task.moderated_reason && <span style={{ marginTop: 3, fontWeight: 700 }}>{task.moderated_reason}</span>}
      </div>
    );
  }

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=''
        style={{ width: 52, height: 92, objectFit: 'cover', borderRadius: 8, flexShrink: 0, background: '#000' }}
        loading='lazy'
      />
    );
  }

  if (videoUrl) {
    return (
      <video
        ref={videoRef}
        src={videoUrl}
        style={{ width: 52, height: 92, objectFit: 'cover', borderRadius: 8, flexShrink: 0, background: '#000' }}
        preload='metadata'
        muted
        playsInline
      />
    );
  }

  return (
    <div
      style={{
        width: 52,
        height: 92,
        borderRadius: 8,
        flexShrink: 0,
        background: '#e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div
        style={{
          borderLeft: '10px solid #9ca3af',
          borderTop: '6px solid transparent',
          borderBottom: '6px solid transparent'
        }}
      />
    </div>
  );
};

const TaskCard: React.FC<{ task: PublishTask; onDelete: (id: string) => void }> = ({ task: taskProp, onDelete }) => {
  const [deleting, setDeleting] = React.useState(false);
  const [metrics, setMetrics] = React.useState<Metrics | null>(null);

  // Local override for tasks whose metadata (caption + thumbnail_url) the
  // external_metadata Lambda fills in async after the task is created. We
  // poll the task endpoint until both arrive, then show the fresh version.
  // Without this, the card stays with caption='' and thumbnail_url='' forever
  // until the user hard-refreshes the page.
  const [refreshed, setRefreshed] = React.useState<PublishTask | null>(null);
  const task = refreshed || taskProp;

  // External = task was added via "Añadir con link externo" (no catalog video
  // backing it); the backend creates it with catalog_video_id = ''.
  const isExternal = (task.catalog_video_id ?? '') === '';
  const metadataPending = isExternal && (!task.caption || !task.thumbnail_url);

  React.useEffect(() => {
    if (!metadataPending) return;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 10; // ~30s total
    const INTERVAL_MS = 3000;

    const poll = async () => {
      while (!cancelled && attempts < MAX_ATTEMPTS) {
        await new Promise(r => setTimeout(r, INTERVAL_MS));
        if (cancelled) return;
        try {
          const r = await fetch(`${API_BASE}/drupi/publish-tasks/${task._id}`);
          if (!r.ok) {
            attempts++;
            continue;
          }
          const fresh: PublishTask = await r.json();
          if (fresh.caption && fresh.thumbnail_url) {
            if (!cancelled) setRefreshed(fresh);
            return;
          }
        } catch {
          // network blip — keep trying
        }
        attempts++;
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [metadataPending, task._id]);

  React.useEffect(() => {
    if (task.status !== 'done' || !task.platform_post_id) return;
    // AbortController so unmounting / re-mounting (e.g. user navigates away
    // mid-fetch, or scroll triggers re-mount) doesn't leak inflight requests.
    // With 20+ visible TaskCards this used to leave a long tail of pending
    // /publication-metrics requests that kept their closures alive.
    const ac = new AbortController();
    let cancelled = false;
    const load = () =>
      fetch(`${API_BASE}/drupi/publication-metrics/${task._id}/latest`, { signal: ac.signal })
        .then(r => (r.ok ? r.json() : null))
        .then(data => {
          if (data && !cancelled) setMetrics(data);
        })
        .catch(e => {
          if ((e as { name?: string })?.name === 'AbortError') return;
          // ignore other errors — metrics are best-effort
        });

    load();

    // Post recién agregado: el backend le toma re-snapshots tempranos (+2/+5/+15 min) para
    // que aparezcan sus primeros puntos antes del ciclo horario. Como el fetch de arriba corre
    // una sola vez al montar, sin esto el usuario no los vería sin recargar. Poll liviano solo
    // durante la primera ventana (~18 min) y solo para tasks nuevas; las viejas cargan una vez.
    const EARLY_WINDOW_MS = 18 * 60 * 1000;
    const ageMs = Date.now() - new Date(task.created_at + 'Z').getTime();
    let poll: ReturnType<typeof setInterval> | undefined;
    let stop: ReturnType<typeof setTimeout> | undefined;
    if (Number.isFinite(ageMs) && ageMs >= 0 && ageMs < EARLY_WINDOW_MS) {
      poll = setInterval(load, 60 * 1000);
      stop = setTimeout(() => {
        if (poll) clearInterval(poll);
      }, EARLY_WINDOW_MS - ageMs);
    }

    return () => {
      cancelled = true;
      ac.abort();
      if (poll) clearInterval(poll);
      if (stop) clearTimeout(stop);
    };
  }, [task._id, task.status, task.platform_post_id, task.created_at]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/drupi/publish-tasks/${task._id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) {
        // No borramos la card de la UI si el backend no borró: si no, "desaparece"
        // y al refrescar vuelve. 401/403 = JWT ausente/ajeno; 404 = ya no existe.
        if (res.status === 404) {
          onDelete(task._id); // ya no existía → sacala igual
          return;
        }
        const msg =
          res.status === 401 || res.status === 403
            ? 'No se pudo borrar: volvé a iniciar sesión e intentá de nuevo.'
            : 'No se pudo borrar la publicación. Intentá de nuevo.';
        message.error(msg);
        setDeleting(false);
        return;
      }
      onDelete(task._id);
    } catch {
      message.error('No se pudo borrar la publicación. Revisá tu conexión e intentá de nuevo.');
      setDeleting(false);
    }
  };

  const isIG = task.platform === 'instagram';
  const showMetrics = task.status === 'done' && task.platform_post_id;
  const hasRealMetrics = metrics !== null && Object.values(metrics).some(v => typeof v === 'number' && v > 0);

  return (
    <div
      style={{
        background: '#f9fafb',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 12,
        padding: '16px 20px',
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
        alignItems: 'flex-start'
      }}
    >
      <TaskThumbnail task={task} loading={metadataPending} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minWidth: 0 }}>
        {/* Top row — flexWrap por mobile estrecho: si los tags + fecha no
          entran en una línea, la fecha cae a línea aparte en lugar de
          comprimir el contenedor hasta que el browser rompe el Text en
          caracteres. */}
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {(() => {
              const ps = PLATFORM_STYLE[task.platform] ?? PLATFORM_STYLE.tiktok;
              return (
                <Tag
                  style={{
                    background: ps.bg,
                    border: `1px solid ${ps.border}`,
                    color: ps.text,
                    fontSize: 11,
                    fontWeight: 700,
                    borderRadius: 6,
                    margin: 0
                  }}
                >
                  {task.platform}
                </Tag>
              );
            })()}
            <Tag
              style={{
                background: task.moderated ? '#fff1f0' : `${STATUS_COLOR[task.status]}22`,
                border: `1px solid ${task.moderated ? '#ffccc7' : `${STATUS_COLOR[task.status]}55`}`,
                color: task.moderated ? '#cf1322' : STATUS_COLOR[task.status],
                fontSize: 11,
                fontWeight: 600,
                borderRadius: 6,
                margin: 0
              }}
            >
              {task.moderated ? 'Invalidada' : STATUS_LABEL[task.status]}
            </Tag>
            {isExternal && (
              <Tag
                icon={<LinkIcon size={11} style={{ marginRight: 4, verticalAlign: -1 }} />}
                style={{
                  background: '#6366f122',
                  border: '1px solid #6366f155',
                  color: '#4f46e5',
                  fontSize: 11,
                  fontWeight: 600,
                  borderRadius: 6,
                  margin: 0,
                  display: 'inline-flex',
                  alignItems: 'center'
                }}
              >
                Externo
              </Tag>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <Text style={{ color: tokens.colors.primary.neutral[500], fontSize: 12, whiteSpace: 'nowrap' }}>
              {new Date(task.created_at + 'Z').toLocaleString('es-AR', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Text>
            {task.result_url && (
              <a href={task.result_url} target='_blank' rel='noopener noreferrer'>
                <ExternalLink size={14} color={tokens.colors.brand.blue} />
              </a>
            )}
            <Popconfirm
              title='¿Borrar esta publicación?'
              description='Dejará de sumar puntos y desaparecerá de tu lista.'
              okText='Borrar'
              cancelText='Cancelar'
              okButtonProps={{ danger: true, loading: deleting }}
              onConfirm={handleDelete}
            >
              <button
                disabled={deleting}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  padding: 2,
                  display: 'flex',
                  alignItems: 'center',
                  opacity: deleting ? 0.4 : 0.6
                }}
                title='Eliminar'
              >
                <Trash2 size={14} color={tokens.colors.primary.neutral[400]} />
              </button>
            </Popconfirm>
          </div>
        </div>

        {/* Error message — humanizes known TikTok / backend error codes; falls
          back to the raw string (truncated) when nothing matches. Raw error
          is hidden behind a "Ver detalle técnico" expander when we DID
          recognize the error, so the user gets the short version first. */}
        {task.status === 'failed' &&
          task.error &&
          (() => {
            const h = humanizePublishError(task.error);
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {h.title && <Text style={{ color: '#ff4d4f', fontSize: 12, fontWeight: 700 }}>{h.title}</Text>}
                <Text style={{ color: '#ff4d4f', fontSize: 12, wordBreak: 'break-word' }}>{h.message}</Text>
                {h.recognized && h.raw && (
                  <details style={{ fontSize: 11, color: tokens.colors.primary.neutral[400] }}>
                    <summary style={{ cursor: 'pointer', userSelect: 'none' }}>Ver detalle técnico</summary>
                    <pre
                      style={{
                        margin: '4px 0 0',
                        padding: '6px 8px',
                        background: 'rgba(0,0,0,0.04)',
                        borderRadius: 6,
                        fontSize: 10,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      {h.raw}
                    </pre>
                  </details>
                )}
              </div>
            );
          })()}

        {/* Caption del social. Para tasks externas (las del picker + las por
          URL) viene de TikTok /v2/video/query o Apify. Para tasks internas
          (publicadas vía nuestro modal) es el que se mandó al publicar. NO se
          gatea con USE_COPY_FROM_SOCIAL: esa flag controla qué caption USAR
          al PUBLICAR, no si mostrar lo que ya está guardado. */}
        {task.caption ? (
          <Text
            style={{
              fontSize: 13,
              lineHeight: 1.4,
              color: tokens.colors.primary.neutral[800],
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {task.caption}
          </Text>
        ) : metadataPending ? (
          <Skeleton.Input active size='small' style={{ width: 220, height: 16 }} />
        ) : null}

        {/* Metrics — only for done tasks with platform_post_id */}
        {showMetrics && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 16,
              paddingTop: 8,
              borderTop: '1px solid rgba(0,0,0,0.07)'
            }}
          >
            {/* Puntos que esta publicación aporta este mes. Misma fórmula que el ranking:
              vistas ganadas desde que la registraste, se reinician a principio de mes.
              Va primero y resaltado porque es la métrica que le importa al usuario. */}
            <Tooltip title='Puntos que suma este mes — 2 vistas ganadas = 1 punto; cuentan desde que registraste la publicación y se reinician a principio de mes'>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: `${tokens.colors.brand.blue}14`,
                  border: `1px solid ${tokens.colors.brand.blue}40`,
                  borderRadius: 8,
                  padding: '3px 10px'
                }}
              >
                <Sparkles size={14} color={tokens.colors.brand.blue} />
                <Text style={{ color: tokens.colors.brand.blue, fontWeight: 800, fontSize: 14 }}>
                  {(metrics?.points ?? 0).toLocaleString()}
                </Text>
                <Text style={{ color: tokens.colors.brand.blue, fontWeight: 600, fontSize: 12 }}>pts</Text>
              </div>
            </Tooltip>

            {hasRealMetrics ? (
              <>
                {isIG ? (
                  <>
                    <Stat icon={<BarChart2 size={14} />} label='Alcance' value={metrics.ig_reach} />
                    <Stat icon={<Heart size={14} />} label='Likes' value={metrics.ig_likes} />
                    <Stat icon={<MessageCircle size={14} />} label='Comentarios' value={metrics.ig_comments} />
                    <Stat icon={<Share2 size={14} />} label='Compartidos' value={metrics.ig_shares} />
                    <Stat icon={<Bookmark size={14} />} label='Guardados' value={metrics.ig_saved} />
                  </>
                ) : (
                  <>
                    <Stat icon={<Eye size={14} />} label='Vistas' value={metrics.tt_view_count} />
                    <Stat icon={<Heart size={14} />} label='Likes' value={metrics.tt_like_count} />
                    <Stat icon={<MessageCircle size={14} />} label='Comentarios' value={metrics.tt_comment_count} />
                    <Stat icon={<Share2 size={14} />} label='Compartidos' value={metrics.tt_share_count} />
                  </>
                )}
                <Text style={{ color: tokens.colors.primary.neutral[500], fontSize: 11, marginLeft: 'auto' }}>
                  Act.{' '}
                  {new Date(metrics.fetched_at + 'Z').toLocaleString('es-AR', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Text>
              </>
            ) : (
              <>
                {isIG ? (
                  <>
                    <Stat icon={<BarChart2 size={14} />} label='Alcance' value={0} />
                    <Stat icon={<Heart size={14} />} label='Likes' value={0} />
                    <Stat icon={<MessageCircle size={14} />} label='Comentarios' value={0} />
                    <Stat icon={<Share2 size={14} />} label='Compartidos' value={0} />
                    <Stat icon={<Bookmark size={14} />} label='Guardados' value={0} />
                  </>
                ) : (
                  <>
                    <Stat icon={<Eye size={14} />} label='Vistas' value={0} />
                    <Stat icon={<Heart size={14} />} label='Likes' value={0} />
                    <Stat icon={<MessageCircle size={14} />} label='Comentarios' value={0} />
                    <Stat icon={<Share2 size={14} />} label='Compartidos' value={0} />
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// NOTE: the old `TiktokPickerVideo` interface, `formatCount` helper,
// `TiktokVideoCard` and `AddExternalModal` components used to live here.
// They were replaced by `SumarPostsModal` (see ../components/sumar-posts-modal),
// which folds picker + URL + (disabled) upload into a single screen and uses
// points-flavored copy ("Sumando puntos") instead of the technical
// "Ya trackeado" / "Agregar publicación externa" labels.

export const MisPublicacionesPage: React.FC = () => {
  const { accounts: allAccounts, loading: accountsLoading } = useSocialContext();
  const accounts = React.useMemo(() => allAccounts.filter(a => isPublishable(a.provider)), [allAccounts]);
  const [tasksByAccount, setTasksByAccount] = React.useState<Record<string, PublishTask[]>>({});
  // Tasks may still be in-flight even after the accounts list has resolved —
  // track that separately so we don't flash the empty state during the fetch.
  const [tasksFetching, setTasksFetching] = React.useState(true);
  // Local open-state for the "Sumá tus posts" modal. Lives here (not in the
  // modal itself) so we can trigger it from the page header and, in the
  // future, from a CTA inside the empty state.
  const [sumarOpen, setSumarOpen] = React.useState(false);
  // Stable array reference passed to the modal — prevents the modal's
  // internal useMemo/useEffect from invalidating on every parent render.
  const modalAccounts = React.useMemo(
    () => accounts.map(a => ({ provider: a.provider, socialId: a.socialId, username: a.username })),
    [accounts]
  );

  React.useEffect(() => {
    // Wait for the global accounts list to settle before deciding anything.
    // Otherwise we'd briefly see "empty" while the context is still loading
    // and then have to re-fetch when accounts arrive — during that window
    // the empty state would flash.
    if (accountsLoading) return;

    if (accounts.length === 0) {
      setTasksFetching(false);
      return;
    }

    // Accounts just became available (or changed): re-arm the loader while
    // we fetch their publish tasks. AbortController + cancelled flag protect
    // against: (a) unmounting before Promise.all resolves leaving inflight
    // requests holding state references, and (b) accounts changing again
    // mid-flight which would otherwise race the two batches into setState.
    setTasksFetching(true);
    const ac = new AbortController();
    let cancelled = false;

    const fetches = accounts.map(acc =>
      fetch(`${API_BASE}/drupi/publish-tasks?social_id=${acc.socialId}&limit=50`, { signal: ac.signal })
        .then(r => r.json())
        .then((tasks: PublishTask[]) => ({ key: `${acc.provider}:${acc.socialId}`, tasks }))
        .catch(() => ({ key: `${acc.provider}:${acc.socialId}`, tasks: [] as PublishTask[] }))
    );

    Promise.all(fetches).then(results => {
      if (cancelled) return;
      const map: Record<string, PublishTask[]> = {};
      results.forEach(r => {
        map[r.key] = r.tasks;
      });
      setTasksByAccount(map);
      setTasksFetching(false);
    });

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [accountsLoading, accounts]);

  const loading = accountsLoading || tasksFetching;
  const allEmpty = Object.values(tasksByAccount).every(t => t.length === 0);

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '40px 24px' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}
        >
          <Title level={2} style={{ margin: 0, color: tokens.colors.primary.neutral[900], fontWeight: 700 }}>
            Mis publicaciones
          </Title>
          {accounts.length > 0 && (
            // Brand-gradient CTA — replaces the "Agregar publicación externa"
            // text link. The wording is positive ("sumá puntos") instead of
            // describing the mechanic ("agregar externa"), and the icon nods
            // to puntos (Sparkles, same icon we use in the "Sumando
            // puntos" tag on the picker cards).
            <Button
              type='primary'
              icon={<Sparkles size={15} />}
              onClick={() => setSumarOpen(true)}
              style={{
                background: tokens.colors.brand.gradient,
                border: 'none',
                fontWeight: 600,
                height: 36,
                paddingInline: 14
              }}
            >
              Sumar mis posts
            </Button>
          )}
        </div>
        <Text style={{ color: tokens.colors.primary.neutral[500], fontSize: 15, marginTop: 8, display: 'block' }}>
          Todas tus publicaciones en redes sociales, en cualquier estado.
        </Text>
      </div>

      {/* Modal lives outside the header so it can also be triggered from the
          empty state below (or from anywhere else on the page in the future). */}
      <SumarPostsModal
        accounts={modalAccounts}
        open={sumarOpen}
        onClose={() => setSumarOpen(false)}
        onAdded={task => {
          const platform = String(task.platform ?? '');
          const socialId = String(task.social_id ?? '');
          if (!platform || !socialId) return;
          const key = `${platform}:${socialId}`;
          setTasksByAccount(prev => ({
            ...prev,
            [key]: [task as unknown as PublishTask, ...(prev[key] || [])]
          }));
        }}
      />

      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin size='large' />
        </div>
      ) : accounts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <FileText size={40} color={tokens.colors.primary.neutral[400]} />
          <Text style={{ display: 'block', marginTop: 16, color: tokens.colors.primary.neutral[500] }}>
            Conectá una cuenta para ver tus publicaciones
          </Text>
        </div>
      ) : allEmpty ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <FileText size={40} color={tokens.colors.primary.neutral[400]} />
          <Text style={{ display: 'block', marginTop: 16, color: tokens.colors.primary.neutral[500] }}>
            Todavía no sumaste ningún post
          </Text>
          <Text
            style={{
              display: 'block',
              marginTop: 8,
              color: tokens.colors.primary.neutral[400],
              fontSize: 13,
              maxWidth: 360,
              marginInline: 'auto',
              lineHeight: 1.5
            }}
          >
            Si ya publicaste algo mencionando a {SPONSOR_NAME}, agregalo acá y empezás a ganar puntos por sus vistas y
            likes.
          </Text>
          <Button
            type='primary'
            icon={<Sparkles size={15} />}
            onClick={() => setSumarOpen(true)}
            style={{
              marginTop: 18,
              background: tokens.colors.brand.gradient,
              border: 'none',
              fontWeight: 600
            }}
          >
            Sumar mis posts
          </Button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {accounts.map(acc => {
            const key = `${acc.provider}:${acc.socialId}`;
            const tasks = tasksByAccount[key] || [];
            if (tasks.length === 0) return null;
            return (
              <div key={key}>
                <Text
                  style={{
                    color: tokens.colors.primary.neutral[600],
                    fontWeight: 700,
                    fontSize: 13,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block',
                    marginBottom: 10
                  }}
                >
                  @{acc.username} · {acc.provider}
                </Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {tasks.map(task => (
                    <TaskCard
                      key={task._id}
                      task={task}
                      onDelete={id =>
                        setTasksByAccount(prev => ({
                          ...prev,
                          [key]: prev[key].filter(t => t._id !== id)
                        }))
                      }
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
